/**
 * OPBridge's controller registry, from OPButtons' side (DEC-256).
 *
 * OPBridge learns every MIDI controller once and gives each control a
 * permanent id and a renamable code (`NP1-P3`). A binding `(page, control)
 * -> named action` lives in OPBridge, which fires the action on a press -
 * so a pedal works with the iPad asleep. OPButtons only PICKS: it reads the
 * registry and writes bindings (`opbuttons-full` v22: `/controllers` to read,
 * `/controllers/bind` to write a binding or switch the page; units and pages
 * are OPBridge's and refuse our key with 403).
 *
 * The registry is replicated mesh-wide (`controllers.delta`), so ANY
 * connected bridge answers for all of them, controllers plugged into other
 * PCs included. This module asks the primary first, then the others.
 */

import { bridges, getConnection, primaryBridge } from './store.ts';
import {
  OpbridgeError, type ControllerRegistry, type ControllerBindingWrite, type ControlKind,
  type OpbridgeHttpClient,
} from './httpClient.ts';

export type RegistryRead =
  | { ok: true; registry: ControllerRegistry; bridgeId: string }
  | { ok: false; why: 'no-bridge' | 'not-allowed' | 'failed'; detail: string };

/** Connected bridges, primary first. */
function connectedClients(): Array<{ bridgeId: string; client: OpbridgeHttpClient }> {
  const primary = primaryBridge()?.id;
  const ordered = [...bridges()].sort((a, b) => (a.id === primary ? -1 : b.id === primary ? 1 : 0));
  const out: Array<{ bridgeId: string; client: OpbridgeHttpClient }> = [];
  for (const b of ordered) {
    const conn = getConnection(b.id);
    if (conn && conn.status() === 'connected') out.push({ bridgeId: b.id, client: conn.httpClient() });
  }
  return out;
}

function refusal(err: unknown): boolean {
  return err instanceof OpbridgeError && (err.kind === 'unauthorized' || err.httpStatus === 403 || err.httpStatus === 404);
}

/** Read the registry from the first connected bridge that answers. A 401 /
 *  403 / 404 everywhere means this OPBridge does not let OPButtons read
 *  controllers yet (before `opbuttons-full` v22). */
export async function readRegistry(): Promise<RegistryRead> {
  const clients = connectedClients();
  if (clients.length === 0) return { ok: false, why: 'no-bridge', detail: 'No OPBridge is connected.' };
  let last: unknown = null;
  let allRefused = true;
  for (const { bridgeId, client } of clients) {
    try {
      const registry = await client.getControllers();
      return {
        ok: true, bridgeId,
        registry: {
          units: registry.units ?? [], pages: registry.pages ?? [], bindings: registry.bindings ?? [],
          ...(registry.activePageId ? { activePageId: registry.activePageId } : {}),
        },
      };
    } catch (err) {
      last = err;
      if (!refusal(err)) allRefused = false;
    }
  }
  const detail = last instanceof Error ? last.message : String(last);
  return allRefused
    ? { ok: false, why: 'not-allowed', detail: 'This OPBridge does not let OPButtons use controllers yet. Update OPBridge.' }
    : { ok: false, why: 'failed', detail };
}

/** Write one binding through `bridgeId` (the one that answered the read),
 *  falling back to any other connected bridge. */
export async function writeBinding(b: ControllerBindingWrite, bridgeId?: string): Promise<void> {
  const clients = connectedClients().sort((x, y) => (x.bridgeId === bridgeId ? -1 : y.bridgeId === bridgeId ? 1 : 0));
  if (clients.length === 0) throw new OpbridgeError('network', 'No OPBridge is connected.');
  let last: unknown = null;
  for (const { client } of clients) {
    try { await client.putControllerBinding(b); return; } catch (err) { last = err; }
  }
  throw last instanceof Error ? last : new Error(String(last));
}

/** Remove every binding (on every page) whose action is one of `actionIds`.
 *  Best effort: a bridge that cannot be read or written leaves them. Used
 *  when an item a controller was assigned to is deleted (`modShare/sync.ts`). */
export async function unbindActions(actionIds: Set<string>): Promise<number> {
  if (actionIds.size === 0) return 0;
  const read = await readRegistry().catch(() => null);
  if (!read || !read.ok) return 0;
  let removed = 0;
  for (const b of read.registry.bindings) {
    if (!actionIds.has(b.actionId)) continue;
    try {
      await writeBinding({ pageId: b.pageId, controlId: b.controlId }, read.bridgeId);
      removed++;
    } catch { /* left for the dashboard */ }
  }
  return removed;
}

/** Controls that send a VALUE (a knob's turn, a pedal's sweep). OPBridge
 *  fires their binding with `{values: {<input>: {fraction}}}`, which an
 *  action without inputs refuses (400) - so a value control may only take an
 *  action with an input, and a switch only one without. */
export function sendsValue(kind: ControlKind | string): boolean {
  return kind === 'knob' || kind === 'fader' || kind === 'expression' || kind === 'wheel';
}

/** "footswitch 3", "knob 1": a control's kind in words, for people. */
export function kindWord(kind: ControlKind | string): string {
  switch (kind) {
    case 'footswitch': return 'footswitch';
    case 'button': return 'button';
    case 'knob': return 'knob';
    case 'fader': return 'fader';
    case 'expression': return 'expression pedal';
    case 'wheel': return 'wheel';
    default: return String(kind);
  }
}
